<template>
  <div id="Tiles" :class="[configuratorOpen ? 'open' : 'closed']">
    <div class="top-row" :class="{ bordered: showDivider }" @click="onConfiguratorClick">
      <div class="name-row">
        <span class="title">{{ configurationGroup.name }}</span>
        <span v-if="displayed" class="select-title">{{ displayed.value }}</span>
        <span v-else-if="selected" class="select-title">{{ selected.value }}</span>
      </div>
      <span v-if="isStackedConfigurator" class="af af-plus config-toggle" :class="[configuratorOpen ? 'open' : 'closed']"></span>
    </div>
    <div ref="configuratorOptions">
      <transition name="configurator-value-container">
        <div
          v-if="(isStackedConfigurator && configuratorOpen) || !isStackedConfigurator"
          ref="valueRows"
          class="value-rows"
          :class="[isShownMore && 'showMore', configurationGroup.configuration.length == 1 && 'cursor-arrow', showDivider && 'bordered']"
        >
          <div
            v-for="(config, index) of configurationGroup.configuration"
            :key="index"
            class="check"
            :class="[selected && selected.value == config.value && 'active']"
            @click.passive="onCheckChange($event, config)"
            @mouseover="onCheckHover($event, config)"
            @mouseout="onCheckMouseOut()"
          >
            <img v-if="checkImage(config.imageUrl) == 'svg'" :src="config.imageUrl" :alt="config.value" />
            <responsive-image v-else :image="{ url: config.imageUrl }" />
          </div>
        </div>
        <a v-if="toShowMore" class="more" @click.prevent="toggleMore()">
          <span class="text">{{ isShownMore ? 'Less Option' : 'More Option' }}</span>
          <span class="af af-chevron-down option-arrow" :class="[isShownMore ? 'arrow-up' : null]"></span>
        </a>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import smoothReflow from 'vue-smooth-reflow';

import { Image, MediaUtils } from '@article/pixel';

export default {
  name: 'Tiles',
  components: {
    'responsive-image': Image,
  },
  mixins: [smoothReflow],
  props: {
    configurationGroup: Object,
    configuratorId: Number,
    isStackedConfigurator: {
      type: Boolean,
      default: false,
    },
    showDivider: Boolean,
  },
  data() {
    return {
      displayed: null,
      selected: null,
      isMounted: false,
      isShownMore: false,
      rows: [],
      toShowMore: false,
      rowsMaxHeight: 76,
    };
  },
  computed: {
    ...mapState('product', [
      'currentOpenConfigurator', //
    ]),
    ...mapGetters('product', [
      'configurationsLength', //
    ]),
    configuratorOpen() {
      return this.configuratorId === this.currentOpenConfigurator;
    },
  },
  watch: {
    configurationGroup: {
      deep: true,
      handler() {
        this.formatSelected();
        this.checkRowsHeight();
      },
    },
  },
  created() {
    this.formatSelected();
  },
  mounted() {
    this.isMounted = true;
    this.checkRowsHeight();

    MediaUtils.isTabletAndGreater &&
      this.$smoothReflow({
        el: this.$refs.configuratorOptions,
        property: 'height',
        transition: 'height 150ms ease-in-out',
      });
  },
  methods: {
    ...mapMutations('product', [
      'updateCurrentOpenConfigurator', //
    ]),
    toggleMore() {
      this.isShownMore = !this.isShownMore;
    },
    formatSelected() {
      this.selected = this.configurationGroup.configuration.find((config) => config.selected);
    },
    checkImage(path) {
      if (typeof path === 'string') {
        const arr = path.split('.');
        return arr[arr.length - 1];
      }
      return null;
    },
    onCheckChange(event, value) {
      if (this.selected && this.selected.valueId !== value.valueId) {
        this.$emit('input', {
          id: this.configurationGroup.attributeId,
          configurationGroup: this.configurationGroup,
          value,
        });
        this.displayed = null;
      }
    },
    onCheckHover(event, value) {
      this.displayed = { ...value };
    },
    onCheckMouseOut() {
      this.displayed = null;
    },
    checkRowsHeight() {
      if (this.isMounted && MediaUtils.isMobileAndGreater) {
        this.$nextTick(() => {
          const { valueRows } = this.$refs;
          if (valueRows) {
            // const { clientHeight, scrollHeight } = valueRows;
            // this.toShowMore = clientHeight < scrollHeight || clientHeight > this.rowsMaxHeight;
            const { clientHeight } = valueRows;
            this.toShowMore = clientHeight > this.rowsMaxHeight;
          } else {
            this.toShowMore = false;
          }
        });
      }
    },
    onConfiguratorClick() {
      if (this.isStackedConfigurator) {
        this.updateCurrentOpenConfigurator(this.configuratorId);
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

#Tiles {
  display: flex;
  flex-direction: column;

  .top-row {
    display: flex;
    justify-content: space-between;
  }

  .name-row {
    display: flex;
    align-items: baseline;

    .title {
      margin-right: 12px;
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: var(--font-size-14);
    }

    .select-title {
      font-family: var(--font-light);
      font-size: var(--font-size-14);
      line-height: 1em;
    }

    @media (max-width: $mobile-breakpoint - 1) {
      flex-direction: column;

      .select-title {
        margin-top: 4px;
        margin-bottom: 2px;
      }
    }

    @media (min-width: $mobile-breakpoint) {
      margin-bottom: 8px;
    }
  }

  .value-rows {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    transform: translateZ(0);
    margin-top: 4px;

    transition: max-height ease-out 150ms;

    &.showMore {
      max-height: 240px !important;
    }

    &.cursor-arrow {
      .check {
        cursor: initial;
      }
    }

    @media (max-width: $mobile-breakpoint - 1) {
      overflow-x: auto;
      flex-wrap: unset;
    }

    @media (min-width: $mobile-breakpoint) {
      max-height: 76px;
      overflow: hidden;
    }

    .check {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 66px;
      height: 66px;
      margin: 4px;
      box-sizing: border-box;
      border: 1px solid rgba(0, 0, 0, 0);
      border-radius: 8px;
      cursor: pointer;

      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

      transition: border linear 100ms;

      &:hover,
      &.active {
        border: 1px solid rgba(255, 100, 88, 1);
      }

      @media (max-width: $mobile-breakpoint - 1) {
        flex: 1 0 22%;
        max-width: 100px;
        margin: 4px 0px;
        padding: 12px;
        display: flex;
        justify-content: center;
        align-items: center;

        .img {
          height: 100%;
        }
      }
    }
  }

  .more {
    position: absolute;
    display: flex;
    align-items: center;
    right: 20px;
    bottom: -32px;

    .text {
      margin-right: 8px;
    }

    .option-arrow {
      transition: transform linear 200ms;

      &.arrow-up {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
